import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import "./BookingForm.scss";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PhoneInput from "./PhoneInput";
import GuestIdUpload, { passengersComplete } from "./GuestIdUpload";
import compressImage from "./compressImage";
import { cairoDateTime, meetsVerificationLeadTime, earliestVerifiedDate } from "./tripTime";

const BACKEND = process.env.REACT_APP_BACKEND;
const DEPOSIT_RATIO = 0.25;

function BookingForm() {
  const boatId = useParams();
  const navigate = useNavigate();

  const [theme, setTheme] = useState(() => localStorage.getItem('bookingTheme') || 'dark');
  const [isPopupOpen, setIsPopupOpen] = useState(true);
  const [boatData, setBoatData] = useState(null);
  const [boatName, setBoatName] = useState("");
  const [images, setImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const [fullDay, setFullDay] = useState(false);
  const [slots, setSlots] = useState({ slot1: false, slot2: false, slot3: false });
  const [summerTime, setSummerTime] = useState(false);
  const [allTimeSlots, setAllTimeSlots] = useState([
    { label: 'Slot 1', start_time: '09:00', end_time: '12:00' },
    { label: 'Slot 2', start_time: '12:00', end_time: '17:30' },
    { label: 'Slot 3', start_time: '17:30', end_time: '20:40' },
  ]);
  const [allSummerTimeSlots, setAllSummerTimeSlots] = useState([
    { label: 'Slot 1', start_time: '09:00', end_time: '13:00' },
    { label: 'Slot 2', start_time: '13:00', end_time: '18:00' },
  ]);
  const [allHolidays, setAllHolidays] = useState([]);

  const [formData, setFormData] = useState({
    bookedBy: "", lastName: "", email: "", number: "",
    destination: "", date: "", slot1: false, slot2: false, slot3: false,
    code: "", status: "Customer", dropOff: false, money: 0, outsideGuests: false,
  });
  const [passengers, setPassengers] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // ── Fetch boat + settings ─────────────────────────────────────────
  useEffect(() => {
    const fetchBoat = async () => {
      try {
        const res = await axios.get(`${BACKEND}/api/boats/${boatId.id}`);
        setBoatData(res.data);
        setBoatName(res.data.name);
        setImages(res.data.images);
      } catch (error) {
        console.error(error);
      }
    };
    fetchBoat();
  }, [boatId]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${BACKEND}/api/settings`);
        setSummerTime(res.data.summerTime);
        setAllTimeSlots(res.data.timeSlots && res.data.timeSlots.length > 0 ? res.data.timeSlots : [
          { label: 'Slot 1', start_time: '09:00', end_time: '12:00' },
          { label: 'Slot 2', start_time: '12:00', end_time: '17:30' },
          { label: 'Slot 3', start_time: '17:30', end_time: '20:40' },
        ]);
        setAllSummerTimeSlots(res.data.summerTimeSlots && res.data.summerTimeSlots.length > 0 ? res.data.summerTimeSlots : [
          { label: 'Slot 1', start_time: '09:00', end_time: '13:00' },
          { label: 'Slot 2', start_time: '13:00', end_time: '18:00' },
        ]);
      } catch (error) {
        console.error(error);
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        const res = await axios.get(`${BACKEND}/api/holidays`);
        setAllHolidays(res.data);
      } catch (error) {
        console.error(error);
      }
    };
    fetchHolidays();
  }, []);

  // ── Pricing (display only) ──────────────────────────────────────────
  // The server's computeTripPrice in server/utils/pricing.js is the source of
  // truth and rejects a checkout whose price differs from it. The three helpers
  // below are a display copy of it and must stay in sync with that file.

  // ── Helper: Check if date is in a holiday range ────────────────────
  const getApplicableHolidayPricing = (date) => {
    if (!date || !allHolidays || allHolidays.length === 0) return null;
    const selectedDate = new Date(date);
    selectedDate.setUTCHours(0, 0, 0, 0);

    const applicable = allHolidays
      .filter((h) => {
        const start = new Date(h.start_date);
        const end = new Date(h.end_date);
        start.setUTCHours(0, 0, 0, 0);
        end.setUTCHours(0, 0, 0, 0);
        return selectedDate >= start && selectedDate <= end;
      })
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    if (applicable.length === 0) return null;
    // Pricing is now keyed by boatId, return only this boat's pricing
    return applicable[0].pricing?.[boatId.id] || null;
  };

  // ── Helper: Check if exactly 2 slots are selected (2-slot bundle) ─────
  const isTwoSlotBundle = () => {
    const selectedCount = (formData.slot1 ? 1 : 0) + (formData.slot2 ? 1 : 0) + (formData.slot3 ? 1 : 0);
    return selectedCount === 2; // Any pair of 2 slots = 2-slot bundle
  };

  // ── DB-driven pricing with holiday override ────────────────────────
  useEffect(() => {
    if (!formData.destination) {
      setFormData((prev) => ({ ...prev, money: 0 }));
      return;
    }

    // Select correct pricing based on season
    const basePricing = summerTime ? (boatData?.summerPricing || {}) : (boatData?.pricing || {});
    const holidayPricingObj = getApplicableHolidayPricing(formData.date);

    // If holiday pricing exists, extract the correct season's pricing
    let p = basePricing;
    if (holidayPricingObj) {
      // Holiday pricing might have summer_ prefix for summer season fields
      // Create a transformed pricing object that uses the right fields based on season
      p = {};
      Object.keys(holidayPricingObj).forEach((destKey) => {
        if (typeof holidayPricingObj[destKey] === 'object') {
          if (summerTime) {
            // Use summer fields (summer_slot1, summer_slot2, etc.)
            p[destKey] = {
              slot1: holidayPricingObj[destKey].summer_slot1 ?? holidayPricingObj[destKey].slot1 ?? 0,
              slot2: holidayPricingObj[destKey].summer_slot2 ?? holidayPricingObj[destKey].slot2 ?? 0,
              fullDay: holidayPricingObj[destKey].summer_fullDay ?? holidayPricingObj[destKey].fullDay ?? 0,
              full: holidayPricingObj[destKey].summer_full ?? holidayPricingObj[destKey].full ?? 0,
              dropOff: holidayPricingObj[destKey].summer_dropOff ?? holidayPricingObj[destKey].dropOff ?? 0,
            };
          } else {
            // Use normal fields (slot1, slot2, slot3, etc.)
            p[destKey] = holidayPricingObj[destKey];
          }
        }
      });
    }

    if (!p || Object.keys(p).length === 0) {
      setFormData((prev) => ({ ...prev, money: 0 }));
      return;
    }

    const dest = formData.destination.toLowerCase();
    const { slot1, slot2, slot3, dropOff } = formData;
    let cost = 0;

    // Check if this destination is slot-based
    const destinationObj = boatData?.destinations?.find((d) => (typeof d === "string" ? d : d.name)?.toLowerCase() === dest);
    const isSlotBased = destinationObj?.isSlotBased ?? false;

    if (isSlotBased) {
      // Slot-based pricing
      const isFullDay = summerTime ? (slot1 && slot2) : (slot1 && slot2 && slot3);
      if (isFullDay) {
        cost = p[dest]?.fullDay || 0;
      } else if (isTwoSlotBundle()) {
        // 2-slot bundle (slots 1 and 2 selected, slot 3 not selected)
        cost = p[dest]?.twoSlot || (p[dest]?.slot1 || 0) + (p[dest]?.slot2 || 0);
      } else {
        if (slot1) cost += p[dest]?.slot1 || 0;
        if (slot2) cost += p[dest]?.slot2 || 0;
        if (!summerTime && slot3) cost += p[dest]?.slot3 || 0;
      }
    } else {
      // Full-day pricing
      cost = dropOff ? (p[dest]?.dropOff || 0) : (p[dest]?.full || 0);
    }

    setFormData((prev) => ({ ...prev, money: cost }));
  }, [formData.destination, formData.slot1, formData.slot2, formData.slot3, formData.dropOff, boatData, summerTime, formData.date, allHolidays, allTimeSlots]);

  // ── Destinations ──────────────────────────────────────────────────
  const validDestinations = () => {
    if (!boatData?.destinations || boatData.destinations.length === 0) {
      return [];
    }
    // Map destination objects (which have _id and name) to just names
    return boatData.destinations.map((d) => (typeof d === "string" ? d : d.name));
  };

  // ── Helper: Build human-readable time range for selected slots ─────
  const getTimeText = () => {
    const activeSlots = summerTime ? allSummerTimeSlots : allTimeSlots;
    const selectedIndices = [];
    if (formData.slot1) selectedIndices.push(0);
    if (formData.slot2) selectedIndices.push(1);
    if (!summerTime && formData.slot3) selectedIndices.push(2);

    if (selectedIndices.length === 0) return formData.dropOff ? "Drop-off" : "";

    const fmt = (time) => {
      const [h, m] = time.split(":");
      const hour = parseInt(h);
      const ampm = hour >= 12 ? "PM" : "AM";
      return `${hour % 12 || 12}:${m} ${ampm}`;
    };

    const first = activeSlots[selectedIndices[0]];
    const last = activeSlots[selectedIndices[selectedIndices.length - 1]];
    if (!first || !last) return "";
    return `${fmt(first.start_time)} – ${fmt(last.end_time)}`;
  };

  // ── Helper: Get formatted slot label ───────────────────────────────
  const getSlotLabel = (slotNum) => {
    // Use summer slots if summer time is active, otherwise use normal slots
    const slots = summerTime ? allSummerTimeSlots : allTimeSlots;

    if (!slots || slots.length === 0) {
      // Fallback to hardcoded if not loaded
      const defaults = {
        1: "9:00 am – 12:00 pm",
        2: "12:00 pm – 5:30 pm",
        3: "3:40 pm – 6:40 pm",
      };
      return defaults[slotNum] || "";
    }
    const slot = slots[slotNum - 1];
    if (!slot) return "";
    // Convert 24-hour to 12-hour format
    const formatTime = (time) => {
      const [h, m] = time.split(":");
      const hour = parseInt(h);
      const ampm = hour >= 12 ? "pm" : "am";
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${m} ${ampm}`;
    };
    return `${slot.label || `Slot ${slotNum}`} (${formatTime(slot.start_time)} – ${formatTime(slot.end_time)})`;
  };

  const isSlotPastForDate = (slotNum, dateStr) => {
    const _now = new Date();
    const todayStr = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, "0")}-${String(_now.getDate()).padStart(2, "0")}`;
    if (dateStr !== todayStr) return false;
    const activeSlots = summerTime ? allSummerTimeSlots : allTimeSlots;
    const slot = activeSlots[slotNum - 1];
    if (!slot) return false;
    const now = new Date();
    const [h, m] = slot.start_time.split(':').map(Number);
    const slotStart = new Date();
    slotStart.setHours(h, m, 0, 0);
    return now > slotStart;
  };

  // ── Passengers from outside El Gouna: 48h lead time ───────────────
  const slotStartFor = (slotNum, dateStr) => {
    const activeSlots = summerTime ? allSummerTimeSlots : allTimeSlots;
    const slot = activeSlots[slotNum - 1] || activeSlots[0];
    return cairoDateTime(dateStr, slot?.start_time || "00:00");
  };

  const isSlotTooSoonForDate = (slotNum, dateStr, outsideGuests = formData.outsideGuests) =>
    !!outsideGuests && !!dateStr && !meetsVerificationLeadTime(slotStartFor(slotNum, dateStr));

  // Earliest selected slot, matching the server's getTripStart
  const tripStart = () => {
    const first = [formData.slot1, formData.slot2, formData.slot3].findIndex(Boolean);
    return slotStartFor(first >= 0 ? first + 1 : 1, formData.date);
  };

  const leadTimeOk = !formData.outsideGuests || !formData.date || meetsVerificationLeadTime(tripStart());

  const handleOutsideGuestsToggle = (checked) => {
    setFormData((prev) => {
      const next = { ...prev, outsideGuests: checked };
      // Drop slots that no longer leave 48 hours for ID approval
      if (checked && prev.date && getIsSlotBased()) {
        [1, 2, 3].forEach((n) => {
          if (isSlotTooSoonForDate(n, prev.date, true)) next[`slot${n}`] = false;
        });
      }
      return next;
    });
  };

  // ── Form handlers ─────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleDateChange = async (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value, slot1: false, slot2: false, slot3: false }));

    const newDate = name === "date" ? value : formData.date;
    const newDestination = name === "destination" ? value : formData.destination;
    if (!newDate || !newDestination) return;

    const destinationObj = boatData?.destinations?.find(
      (d) => (typeof d === "string" ? d : d.name)?.toLowerCase() === newDestination.toLowerCase()
    );
    const isSlotBasedDest = destinationObj?.isSlotBased ?? false;

    try {
      const { data } = await axios.get(
        `${BACKEND}/api/booking/available/${boatName}/${newDate}`
      );

      if (isSlotBasedDest) {
        setFullDay(false);
        setSlots({ slot1: data.slot1, slot2: data.slot2, slot3: data.slot3 });
      } else {
        // For full-day destinations, require all active (non-past) slots to be free
        const s1 = data.slot1 && !isSlotPastForDate(1, newDate) && !isSlotTooSoonForDate(1, newDate);
        const s2 = data.slot2 && !isSlotPastForDate(2, newDate);
        const s3 = data.slot3 && !isSlotPastForDate(3, newDate);
        const allAvailable = summerTime ? (s1 && s2) : (s1 && s2 && s3);

        if (allAvailable) {
          setFullDay(true);
          setSlots(data);
          setFormData((prev) => ({
            ...prev,
            slot1: true,
            slot2: true,
            slot3: summerTime ? false : true,
          }));
        } else {
          setFullDay(false);
          setSlots({ slot1: false, slot2: false, slot3: false });
        }
      }
    } catch (error) {
      toast.error("Failed to load slots, please refresh the page.");
      console.error(error);
    }
  };

  const isFormValid = () => {
    const baseValid = !!(formData.bookedBy && formData.email && formData.number && formData.destination && formData.date);
    const dest = formData.destination.toLowerCase();
    const destinationObj = boatData?.destinations?.find(
      (d) => (typeof d === "string" ? d : d.name)?.toLowerCase() === dest
    );
    const isSlotBasedDest = destinationObj?.isSlotBased ?? false;

    const guestsValid = !formData.outsideGuests || (passengersComplete(passengers) && leadTimeOk);

    if (isSlotBasedDest) {
      const hasSlot = formData.slot1 || formData.slot2 || (!summerTime && formData.slot3);
      return !!(baseValid && hasSlot && guestsValid);
    }
    return !!(baseValid && fullDay && guestsValid);
  };

  // Uploads the ID images first; the returned token travels through Paymob
  // and links the images to the booking when the payment webhook arrives.
  const uploadGuestIds = async () => {
    const data = new FormData();
    data.append("boatId", boatId.id);
    for (const [i, p] of passengers.entries()) {
      data.append(`name_${i}`, p.name.trim());
      data.append(`front_${i}`, await compressImage(p.front), `front_${i}.jpg`);
      data.append(`back_${i}`, await compressImage(p.back), `back_${i}.jpg`);
    }
    const res = await axios.post(`${BACKEND}/api/guest-ids`, data, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data.token;
  };

  // ── Checkout ──────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    localStorage.setItem("userEmail", formData.email);
    localStorage.setItem("name", formData.bookedBy);
    localStorage.setItem("lastName", formData.lastName);
    localStorage.setItem("number", formData.number);
    localStorage.setItem("amount", formData.money);
    localStorage.setItem("boat", boatName);
    localStorage.setItem("date", formData.date);
    localStorage.setItem("outsideGuests", formData.outsideGuests ? "true" : "false");

    if (!isFormValid()) {
      toast.error("Please complete the form.");
      return;
    }
    setSubmitting(true);
    try {
      const guestIdToken = formData.outsideGuests ? await uploadGuestIds() : undefined;
      const intention = await axios.post(`${BACKEND}/api/paymob`, {
        ...formData, boat: boatName, boatID: boatId.id, timeText: getTimeText(), guestIdToken,
      });
      window.location.href = intention.data.url;
    } catch (error) {
      toast.error(error.response?.data?.message || "Checkout failed");
      console.error(error);
      setSubmitting(false);
    }
  };

  // ── Popup ─────────────────────────────────────────────────────────
  const handleClose = () => { setIsPopupOpen(false); navigate(-1); };
  const handleOutsideClick = () => {};

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === "Escape") { setIsPopupOpen(false); navigate(-1); }
    };
    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [navigate]);


  if (!isPopupOpen) return null;

  const _todayDate = new Date();
  const today = `${_todayDate.getFullYear()}-${String(_todayDate.getMonth() + 1).padStart(2, "0")}-${String(_todayDate.getDate()).padStart(2, "0")}`;
  const minDate = formData.outsideGuests && earliestVerifiedDate() > today ? earliestVerifiedDate() : today;

  const isSlotPast = (slotNum) => isSlotPastForDate(slotNum, formData.date);
  const isSlotTooSoon = (slotNum) => !isSlotPast(slotNum) && isSlotTooSoonForDate(slotNum, formData.date);

  // ── Carousel ──────────────────────────────────────────────────────
  const nextSlide = () => setCurrentIndex((i) => (i === images.length - 1 ? 0 : i + 1));
  const prevSlide = () => setCurrentIndex((i) => (i === 0 ? images.length - 1 : i - 1));

  // Determine if this destination is slot-based
  const getIsSlotBased = () => {
    if (!formData.destination) return false;
    const dest = formData.destination.toLowerCase();
    const destinationObj = boatData?.destinations?.find((d) => (typeof d === "string" ? d : d.name)?.toLowerCase() === dest);
    return destinationObj?.isSlotBased ?? false;
  };
  const isBayoud = getIsSlotBased();

  const toggleTheme = () => {
    const nextTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(nextTheme);
    localStorage.setItem('bookingTheme', nextTheme);
  };

  return (
    <div className="popup-overlay" onClick={handleOutsideClick}>
      <div className="popup-content" data-theme={theme}>
        <div className="popup-header">
          <h1 className="form-title">Book {boatName || "Your Boat"}</h1>
          <button className="theme-toggle" onClick={toggleTheme} aria-label="Toggle theme">
            {theme === 'dark' ? 'Light' : 'Dark'}
          </button>
          <button className="close-button" onClick={handleClose} aria-label="Close booking form">
            &times;
          </button>
        </div>

        {/* Gallery */}
        <div className="-container">
          <h2>Photo Gallery</h2>
          <div className="carousel-wrapper">
            <button className="carousel-button prev" onClick={prevSlide} aria-label="Previous image">
              &#10094;
            </button>
            <div className="carousel">
              <div className="carousel-inner" style={{ transform: `translateX(-${currentIndex * 100}%)` }}>
                {images.length > 0 ? (
                  images.map((image, index) => (
                    <div className="carousel-item" key={index}>
                      <img
                        src={`${BACKEND}/images/boats/${image}`}
                        alt={`${boatName || "Boat"} - Image ${index + 1}`}
                      />
                    </div>
                  ))
                ) : (
                  <div className="carousel-item">
                    <div className="no-images-placeholder">No images available</div>
                  </div>
                )}
              </div>
            </div>
            <button className="carousel-button next" onClick={nextSlide} aria-label="Next image">
              &#10095;
            </button>
          </div>
          <div className="carousel-indicators">
            {images.map((_, index) => (
              <button
                key={index}
                className={`indicator ${index === currentIndex ? "active" : ""}`}
                onClick={() => setCurrentIndex(index)}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Amenities */}
        {boatData?.amenities && boatData.amenities.length > 0 && (
          <div className="amenities-section">
            <h2>What's On Board</h2>
            <div className="amenities-list">
              {boatData.amenities.map((amenity) => (
                <span key={amenity._id || amenity} className="amenity-badge">
                  {amenity.name || amenity}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Booking Form */}
        <form onSubmit={handleSubmit} className="booking-form">
          <h2>Personal Information</h2>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="bookedBy">First Name: <span className="required">*</span></label>
              <input id="bookedBy" type="text" name="bookedBy" value={formData.bookedBy}
                onChange={handleChange} required placeholder="Enter your first name" />
            </div>
            <div className="form-group">
              <label htmlFor="lastName">Last Name: <span className="required">*</span></label>
              <input id="lastName" type="text" name="lastName" value={formData.lastName}
                onChange={handleChange} required placeholder="Enter your last name" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">Email: <span className="required">*</span></label>
              <input id="email" type="email" name="email" value={formData.email}
                onChange={handleChange} required placeholder="example@email.com" />
              <small className="field-hint">Booking confirmation will be sent to this email</small>
            </div>
            <div className="form-group">
              <label htmlFor="number">Phone Number: <span className="required">*</span></label>
              <PhoneInput
                id="number"
                value={formData.number}
                onChange={(val) => setFormData((prev) => ({ ...prev, number: val }))}
                required
                placeholder="Phone number"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="code">Referral Code:</label>
            <input id="code" type="text" name="code" value={formData.code}
              onChange={handleChange} placeholder="Enter referral code if you have one" />
          </div>

          <h2>Passengers</h2>
          <GuestIdUpload
            enabled={formData.outsideGuests}
            onToggle={handleOutsideGuestsToggle}
            passengers={passengers}
            setPassengers={setPassengers}
            capacity={boatData?.capacity}
            leadTimeMessage={leadTimeOk ? "" : "This trip starts in less than 48 hours. Please pick a later date or time so the IDs can be approved."}
          />

          <h2>Trip Details</h2>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="destination">Destination: <span className="required">*</span></label>
              <select id="destination" name="destination" value={formData.destination}
                onChange={handleDateChange} required>
                <option value="">Select a destination</option>
                {validDestinations().map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
              {!formData.destination && (
                <small className="field-hint">Choose from available destinations for this boat</small>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="date">Date: <span className="required">*</span></label>
              <input id="date" type="date" name="date" value={formData.date}
                onChange={handleDateChange} required min={minDate} />
              <small className="field-hint">Select a date for your trip</small>
            </div>
          </div>

          {/* Time slots (Bayoud) */}
          {isBayoud ? (
            <div className="time-slots">
              <h3>Available Time Slots</h3>
              <p className="field-hint">Select your preferred time slot(s) for your trip</p>
              <div className="slot-options">
                <div className="slot-option">
                  <input type="checkbox" id="slot1" name="slot1" checked={formData.slot1}
                    onChange={handleChange} disabled={!slots.slot1 || isSlotPast(1) || isSlotTooSoon(1)} />
                  <label htmlFor="slot1" className={(!slots.slot1 || isSlotPast(1) || isSlotTooSoon(1)) ? "slot-booked" : ""}>
                    {getSlotLabel(1)}
                    {isSlotPast(1) ? <span className="booked-label"> (Past)</span> : !slots.slot1 ? <span className="booked-label"> (Booked)</span> : isSlotTooSoon(1) && <span className="booked-label"> (Needs 48h for ID approval)</span>}
                  </label>
                </div>

                <div className="slot-option">
                  <input type="checkbox" id="slot2" name="slot2" checked={formData.slot2}
                    onChange={handleChange} disabled={!slots.slot2 || isSlotPast(2) || isSlotTooSoon(2)} />
                  <label htmlFor="slot2" className={(!slots.slot2 || isSlotPast(2) || isSlotTooSoon(2)) ? "slot-booked" : ""}>
                    {getSlotLabel(2)}
                    {isSlotPast(2) ? <span className="booked-label"> (Past)</span> : !slots.slot2 ? <span className="booked-label"> (Booked)</span> : isSlotTooSoon(2) && <span className="booked-label"> (Needs 48h for ID approval)</span>}
                  </label>
                </div>

                {!summerTime && (
                  <div className="slot-option">
                    <input type="checkbox" id="slot3" name="slot3" checked={formData.slot3}
                      onChange={handleChange} disabled={!slots.slot3 || isSlotPast(3) || isSlotTooSoon(3)} />
                    <label htmlFor="slot3" className={(!slots.slot3 || isSlotPast(3) || isSlotTooSoon(3)) ? "slot-booked" : ""}>
                      {getSlotLabel(3)}
                      {isSlotPast(3) ? <span className="booked-label"> (Past)</span> : !slots.slot3 ? <span className="booked-label"> (Booked)</span> : isSlotTooSoon(3) && <span className="booked-label"> (Needs 48h for ID approval)</span>}
                    </label>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Full day section (island destinations / Yacht) */
            <div className="full-day-section">
              <h3>Full Day Trip</h3>
              {!formData.date ? (
                <p className="prompt-message">Please select a date to view availability</p>
              ) : fullDay ? (
                <div className="full-day-option">
                  <p className="availability-status available">
                    Full day trip available for selected date
                  </p>
                  <div className="dropoff-option">
                    <input type="checkbox" id="dropOff" name="dropOff" checked={formData.dropOff}
                      onChange={() => setFormData((p) => ({ ...p, dropOff: !p.dropOff }))} />
                    <label htmlFor="dropOff">Drop-off only</label>
                    <small className="field-hint">
                      Check this if you only need transportation to the destination
                    </small>
                  </div>
                </div>
              ) : (
                <p className="availability-status unavailable">
                  Full day trip unavailable for this date. Please select another date.
                </p>
              )}
            </div>
          )}

          {/* Pricing summary */}
          <div className="pricing-section">
            {isFormValid() ? (
              <>
                <h3>Trip Cost Summary</h3>
                <div className="price-details">
                  <div className="price-row">
                    <span>Total Price:</span>
                    <span className="price-value">{formData.money.toLocaleString()} EGP</span>
                  </div>
                  <div className="price-row">
                    <span>Required Deposit (25%):</span>
                    <span className="price-value">
                      {(formData.money * DEPOSIT_RATIO).toLocaleString()} EGP
                    </span>
                  </div>
                  <small className="payment-note">
                    The deposit is required to secure your booking. The remaining balance can be paid on the day of your trip.
                  </small>
                </div>
              </>
            ) : (
              <div className="form-incomplete">
                <p>Please fill in all required fields to see pricing</p>
              </div>
            )}
          </div>

          {/* Booking terms, shown in English and Arabic */}
          <div className="booking-terms">
            <div className="terms-group" lang="en">
              <p>
                By booking, I agree that non-Egyptian guests pay an additional 5 USD (or equivalent) per person,
                in cash, on the day of the trip.
              </p>
              <p>
                In case of cancellation, 5% of the deposit will not be refunded. If the cancellation is less than
                48 hours before the trip, nothing will be refunded.
              </p>
            </div>
            <div className="terms-group" lang="ar" dir="rtl">
              <p>
                بإتمام الحجز، أوافق على أن الضيوف من غير المصريين يدفعون 5 دولارات أمريكية (أو ما يعادلها) للفرد
                نقدًا يوم الرحلة.
              </p>
              <p>
                في حالة الإلغاء، لن يتم استرداد 5% من العربون. وإذا تم الإلغاء قبل أقل من 48 ساعة من موعد الرحلة،
                فلن يتم استرداد أي مبلغ.
              </p>
            </div>
          </div>

          <button type="submit" className="submit-button" disabled={!isFormValid() || submitting}>
            {submitting
              ? (formData.outsideGuests ? "Uploading IDs..." : "Redirecting to Payment...")
              : isFormValid() ? "Proceed to Payment" : "Complete Form to Continue"}
          </button>

          {!isFormValid() && (
            <small className="submit-hint">All required fields must be completed before proceeding</small>
          )}
        </form>
      </div>
    </div>
  );
}

export default BookingForm;
